<template>
  <div class="card mb-2">
    <div class="card-header bg-primary text-white d-inline-flex">
      <a v-b-toggle="`collapse-view-service-card`" class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">Service Details</span>
       <span  class="text-white ml-auto c-pointer">
        <span
          v-if="$store.getters['User/isCurrentUserAdmin']"
          @click="deleteConfirmationModal"
        >
          Delete |
        </span>
        <span @click="editServiceDetails" class="text-white ml-auto c-pointer"
          v-if="!$store.getters['User/isCurrentUserViewOnly']
            && (canUserEdit())"
        >
          Edit
        </span>
      </span>
    </div>
    <b-collapse
      :id="`collapse-view-service-card`"
      v-model="showCollapse"
    >
      <div class="card-body">
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Service ID
          </div>
          <div class="col-sm-8">
            {{ service.id }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Category
          </div>
          <div class="col-sm-8">
            {{ getCategoryName(service.categoryId) }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Component(s)
          </div>
          <div class="col-sm-8">
            {{ componentsString(service) }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Agreement Type
          </div>
          <div class="col-sm-8">
            <template v-if="agreementTypesLoading">
                Loading...
              </template>
              <template v-if="!agreementTypesLoading">
                {{ getAgreementTypeName(service.agreementTypeId) }}
              </template>
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Status
          </div>
          <div class="col-sm-8">
            <font-awesome-icon
              :id="'service-' + service.id"
              icon="circle"
              class="rounded-circle border border-white border-circle"
              size="sm"
              :class="getStatusClass(service.statusId, 'Service')"/>
            {{ getStatusName(service.statusId) }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Service Notes
          </div>
          <div class="col-sm-8 notes-field">{{ service.notes || 'None' }}</div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Staff Lead
          </div>
          <div class="col-sm-8">
            {{ service.staffLeadName }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Service Product(s)
          </div>
          <div class="col-sm-8">
            {{ productsString(service) || 'None' }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Fee Charged
          </div>
          <div class="col-sm-8">
            {{ service.feeCharged | moneyFormat }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Fee Paid
          </div>
          <div class="col-sm-8">
            {{ service.feePaid | formatBooleanAsYesNo }}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Previous Service
          </div>
          <div class="col-sm-8">
            {{ service.previousServiceId || 'None' }}
            <router-link
              v-if="service.previousServiceId"
              class="form-action-text text-secondary"
              tag="a"
              :to="{
                name: 'projectService',
                params: {
                  project_id: $route.params.project_id,
                  id: service.previousServiceId,
                }
              }"
            >
              View
            </router-link>
          </div>
        </div>
      </div>
    </b-collapse>
    <b-modal ref="deleteConfirmation" :id="`${service.id}`" :title="`Delete Service ${service.id}`">
      Are you sure you want to delete this Service? All associated Reviews, links to Contacts, and Events will be deleted.
      <div slot="modal-footer">
        <b-button variant="danger" class="button-row-spacing" @click="hideModal">
          Cancel
        </b-button>
        <b-button @click="onDeleteConfirmed()">
          Delete
        </b-button>
      </div>
    </b-modal>
    <b-modal ref="deleteSuccessModal" :id="`deleteSuccess-${service.id}`" :title="`Successfully Deleted Service ${service.id}`">
      Folders in Box must be deleted manually.
      <div slot="modal-footer">
        <b-button @click="hideDeleteSuccessModal">
          Okay
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { LookupModules } from '@/store/moduleConfig';
import CollapseControl from '@/mixins/CollapseControl';
import StatusHelper from '@/mixins/StatusHelper';

export default {
  name: 'ServiceDetailsCard',
  mixins: [CollapseControl, StatusHelper],
  data() {
    return {
      showCollapse: true,
    };
  },
  computed: {
    ...mapState('Service', {
      service: state => state.currentService,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypesLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.SERVICE_STATUSES.namespace, {
      getStatusName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_CATEGORIES.namespace, {
      getCategoryName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_TYPES.namespace, {
      getTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      getAgreementTypeName: 'getName',
    }),
    ...mapGetters(LookupModules.SERVICE_PRODUCTS.namespace, {
      getProductName: 'getName',
    }),
  },
  methods: {
    productsString(entity) {
      if (entity && entity.productIds) {
        const productIdsCopy = [...entity.productIds];
        return productIdsCopy
          .sort()
          .map(pi => this.getProductName(pi))
          .join(', ');
      }
      return null;
    },
    componentsString(entity) {
      if (entity && entity.typeIds) {
        const typeIdsCopy = [...entity.typeIds];
        return typeIdsCopy
          .sort()
          .map(pi => this.getTypeName(pi))
          .join('; ');
      }
      return null;
    },
    editServiceDetails() {
      this.$store.dispatch('Service/switchToEdit');
      this.$store.dispatch('Common/startEditMode', 'Service');
    },
    canUserEdit() {
      // if user is admin they can always edit
      const roles = this.$store.getters['User/getCurrentUser'].idToken ? this.$store.getters['User/getCurrentUser'].idToken.roles.join(', ') : '';
      if (roles.includes('Admin')) {
        return true;
      }

      // if user is staff AND service is open
      if (roles.includes('Staff') && this.isServiceNewOrOpen(this.service.statusId)) {
        return true;
      }

      // if user is staff AND is staffLead they can always edit
      if (roles.includes('Staff') && this.service.staffLeadName === this.$store.getters['User/getCurrentUser'].name) {
        return true;
      }

      // default return false
      return false;
    },

    isUserAdminOrStaff() {
      const roles = this.$store.getters['User/getCurrentUser'].idToken ? this.$store.getters['User/getCurrentUser'].idToken.roles.join(', ') : '';
      return roles.includes('Admin') || roles.includes('Staff');
    },
    deleteConfirmationModal() {
      this.$refs.deleteConfirmation.show();
    },
    onDeleteConfirmed() {
      this.hideModal();
      this.$store.dispatch('Service/deleteService', { serviceId: this.service.id, submittedCallback: this.showDeleteSuccessModal });
    },
    hideModal() {
      this.$refs.deleteConfirmation.hide();
    },
    showDeleteSuccessModal() {
      this.$refs.deleteSuccessModal.show();
    },
    hideDeleteSuccessModal() {
      this.$refs.deleteSuccessModal.hide();
      this.$store.dispatch('Service/forceFetchServices', this.$store.state.CurrentProject.currentProject.id)
        .then(() => {
          this.$router.push({
            name: 'projectServices',
            params: { project_id: parseInt(this.$route.params.project_id, 10) },
          });
        });
    },
  },
};
</script>
